// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Icon } from "@mirage-web/icons";
import { useOnChangeEffect, useOnUnmountEffect } from "@mirage-web/utils/react-helpers";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { clipsToVideoFile } from "~/modules/mirage/clips-to-video";
import { AppearanceCard } from "~/modules/mirage/components/AppearanceCard";
import { AudioCard } from "~/modules/mirage/components/AudioCard";
import { ClipsViewer } from "~/modules/mirage/components/ClipsViewer";
import { EvalCard } from "~/modules/mirage/components/EvalCard";
import { InferenceJobsCard } from "~/modules/mirage/components/InferenceJobsCard";
import { ScriptCard } from "~/modules/mirage/components/ScriptCard";
import { VideoInfo } from "~/modules/mirage/components/VideoInfo";
import { toggleVideoBookmarkMutation } from "~/modules/mirage/mutations";
import { fetchMirageVideosQuery, lookupMirageVideoByIdQuery } from "~/modules/mirage/queries";

export const Route = createFileRoute("/_dash/videos/view/$videoId")({
  validateSearch: z.object({
    type: z.enum(["user", "org"]).optional(),
    id: z.string().optional(),
    page: z.number().optional(),
    excludeEmployees: z.boolean().optional(),
    from: z.string().optional(),
  }),
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.videoId) {
      throw new Error("Video ID is required");
    }

    queryClient.prefetchQuery(
      lookupMirageVideoByIdQuery.queryOptions({ pathParams: { videoId: params.videoId } })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const { page, type, id, from } = Route.useSearch();
  return (
    <div className="space-y-6">
      <div>
        <Link
          to={from === "bookmarked" ? "/videos/bookmarked" : "/videos"}
          search={from === "bookmarked" ? {} : { page, type, id }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to {from === "bookmarked" ? "Bookmarked" : "All"} Videos
        </Link>
      </div>
      <Skeleton className="h-10 w-100 rounded-md" />
      <div className="flex flex-row gap-2 w-full">
        <div className="flex w-1/3 gap-2">
          <div className="flex flex-col w-1/5 gap-2">
            <Skeleton className="flex h-auto aspect-9/16 w-full rounded-md" />
            <Separator orientation="horizontal" />
            <Skeleton className="flex h-auto aspect-9/16 w-full rounded-md" />
          </div>
          <div className="flex aspect-9/16 w-4/5 h-auto">
            <Skeleton className="flex h-fit aspect-9/16 w-full rounded-md" />
          </div>
        </div>
        <div className="flex flex-col gap-3 w-2/3 h-fit">
          <div className="flex gap-2">
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-8 w-full rounded-md" />
            <Skeleton className="h-8 w-full rounded-md" />
          </div>
          <div className="flex w-full gap-2">
            <Skeleton className="h-40 w-full rounded-md" />
            <Skeleton className="h-40 w-full rounded-md" />
          </div>
          <Skeleton className="h-40 w-full rounded-md" />
          <Skeleton className="h-100 w-full rounded-md" />
        </div>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { videoId } = Route.useParams();
  const { page, type, id, from } = Route.useSearch();

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [clipUrls, setClipUrls] = useState<Map<string, string>>(new Map());
  const [selectedInferenceJobId, setSelectedInferenceJobId] = useState<string | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);

  const { data: video } = lookupMirageVideoByIdQuery.useSuspenseQuery({
    pathParams: {
      videoId,
    },
  });

  // Get the bookmark status from the main feed query if available
  const mainFeedQuery = fetchMirageVideosQuery.useInfiniteQuery({
    params: {
      filters: {},
    },
  });

  const isBookmarkedFromFeed = mainFeedQuery.data?.pages
    .flatMap((page) => page.videos.page)
    .find((v) => v.id === videoId)?.isBookmarked;

  const [isBookmarked, setIsBookmarked] = useState(isBookmarkedFromFeed ?? false);

  const toggleBookmark = toggleVideoBookmarkMutation.useMutation({
    onSuccess: (data) => {
      setIsBookmarked(data.bookmarked);
      toast.success(data.bookmarked ? "Video bookmarked" : "Bookmark removed");
      // Invalidate the feeds to update bookmark status
      mainFeedQuery.refetch();
    },
    onError: () => {
      toast.error("Failed to update bookmark");
    },
  });

  const handleToggleBookmark = () => {
    toggleBookmark.mutate({
      pathParams: { videoId },
      params: {},
    });
  };

  const processVideo = useCallback(async () => {
    if (!selectedInferenceJobId) {
      setSelectedInferenceJobId(video.inferenceJobs?.[0]?.id ?? null);
    }
    if (!selectedSegmentId) {
      setSelectedSegmentId(video.inferenceJobs?.[0]?.segments?.[0]?.id ?? null);
    }

    setClipUrls(new Map(video.clips.map((clip) => [clip.id, clip.url])));
    const compositeFile = await clipsToVideoFile(video.clips.map((clip) => clip.url));
    const objectUrl = URL.createObjectURL(compositeFile);
    setVideoUrl(objectUrl);
  }, [video, selectedInferenceJobId, selectedSegmentId]);

  useOnChangeEffect(video, () => {
    processVideo();
  });

  useOnUnmountEffect(() => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to={from === "bookmarked" ? "/videos/bookmarked" : "/videos"}
          className="text-sm text-muted-foreground hover:underline"
          search={from === "bookmarked" ? {} : { page, type, id }}
        >
          ← Back to {from === "bookmarked" ? "Bookmarked" : "All"} Videos
        </Link>
        <Button
          size="sm"
          variant="outline"
          onClick={handleToggleBookmark}
          disabled={toggleBookmark.isPending}
        >
          <Icon name={isBookmarked ? "saved" : "save"} className="h-4 w-4 mr-2" />
          {isBookmarked ? "Bookmarked" : "Bookmark"}
        </Button>
      </div>
      <div>
        <button
          className="text-xl font-bold cursor-pointer"
          onClick={() => {
            navigator.clipboard.writeText(videoId);
            toast.success("Video ID copied to clipboard");
          }}
        >
          {`Video ID: ${videoId}`}
        </button>
      </div>

      <div className="flex flex-row gap-2 w-full h-full overflow-hidden">
        <div className="flex w-1/3">
          <ClipsViewer
            videoUrl={videoUrl}
            exportUrl={video.exportUrl}
            videoId={videoId}
            clipUrls={clipUrls}
            clips={video.clips}
          />
        </div>

        <div className="flex flex-col gap-3 w-2/3 h-fit text-sm">
          <div className="flex gap-2 w-fit flex-wrap">
            <VideoInfo
              videoId={videoId}
              caller={video.context.caller}
              userId={video.context.userId}
              orgId={video.context.orgId}
              createdAt={video.createdAt}
              completedAt={video.completedAt}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex flex-col gap-3 w-1/2">
              <AudioCard audio={video.input.audio} />
            </div>
            <AppearanceCard appearance={video.input.appearance} />
          </div>

          <ScriptCard
            text={video.input.script.text}
            language={video.input.script.language ?? null}
            videoId={videoId}
          />
          <InferenceJobsCard
            videoId={videoId}
            inferenceJobs={video.inferenceJobs}
            selectedInferenceJobId={selectedInferenceJobId}
            setSelectedInferenceJobId={setSelectedInferenceJobId}
            selectedSegmentId={selectedSegmentId}
            setSelectedSegmentId={setSelectedSegmentId}
          />
          <EvalCard evals={video.evals} />
        </div>
      </div>
    </div>
  );
}
